import React, { useState, useMemo } from 'react';

const ASSET_CLASS_COLORS = {
  'Australian Equities': '#011E41',
  'International Equities': '#5E819C',
  'Global Equities': '#5E819C',
  'Global Equities - H': '#5E819C',
  'Real Estate': '#A6C6C1',
  'Infrastructure': '#337B91',
  'Cash': '#EFE6D5',
  'Government Bonds': '#F3BD48',
  'Fixed Income - D': '#F3BD48',
  'Corporate Debt': '#AA8090',
  'Private Debt': '#874564',
  'Private Equity': '#6C757D',
  'Real Assets (Private Market)': '#127080',
  'Alternatives': '#9B59B6',
  'Emerging Markets': '#34495E',
};

const getAssetClassColor = (assetClass, index) => {
  if (ASSET_CLASS_COLORS[assetClass]) return ASSET_CLASS_COLORS[assetClass];
  const fallback = ['#072040', '#f0a500', '#599BE3', '#91CF9B', '#E74C3C', '#9B59B6', '#1ABC9C', '#E67E22', '#34495E', '#7F8C8D'];
  return fallback[index % fallback.length];
};

const PortfolioBuilder = ({
  funds,
  onUpdateWeight,
  onRemoveFund,
  onSave,
  onSaveAs,
  onClear,
  currentPortfolioId,
  currentPortfolioName,
  currentClient = '',
  currentPortfolioCode = '',
  currentDescription = '',
  currentObjectiveType = 'cpi',
  currentReturnObjective = null,
  currentTimeHorizon = null,
  isDirty,
  gearingEnabled = false,
  onToggleGearing = () => {},
}) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveClient, setSaveClient] = useState('');
  const [savePortfolioCode, setSavePortfolioCode] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  // Objective fields. saveReturnObjective is entered as a percent (e.g. 3.5);
  // converted to a decimal (0.035) on submit to match the stored format.
  const [saveObjectiveType, setSaveObjectiveType] = useState('cpi');
  const [saveReturnObjective, setSaveReturnObjective] = useState('');
  const [saveTimeHorizon, setSaveTimeHorizon] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isSaveAs, setIsSaveAs] = useState(false);

  const totalWeight = useMemo(() => {
    return funds.reduce((sum, f) => sum + (f.weight || 0), 0);
  }, [funds]);

  // Gearing: negative weights are allowed when the toggle is on — the book
  // must still NET to 100. Gross exposure and gearing level are surfaced so
  // a geared book is unmistakable.
  const grossWeight = useMemo(() => {
    return funds.reduce((sum, f) => sum + Math.abs(f.weight || 0), 0);
  }, [funds]);
  const hasNegativeWeights = funds.some(f => (f.weight || 0) < 0);
  const gearingLevel = grossWeight - totalWeight;   // total borrowed/short %

  const hasZeroWeightFunds = funds.some(f => !f.weight || f.weight === 0);
  const negativesBlocked = hasNegativeWeights && !gearingEnabled;
  const grossTooHigh = grossWeight > 300;
  const isValid = Math.abs(totalWeight - 100) <= 0.5 && !hasZeroWeightFunds &&
    !negativesBlocked && !grossTooHigh;

  // Group funds by asset class
  const assetClassGroups = useMemo(() => {
    const groups = {};
    funds.forEach(fund => {
      const ac = fund.asset_class || 'Other';
      if (!groups[ac]) groups[ac] = [];
      groups[ac].push(fund);
    });
    // Only sort by weight when viewing a saved portfolio (not actively editing)
    if (!isDirty) {
      Object.values(groups).forEach(g => g.sort((a, b) => (b.weight || 0) - (a.weight || 0)));
    }
    return groups;
  }, [funds, isDirty]);

  // Asset class totals
  const assetClassTotals = useMemo(() => {
    const totals = {};
    Object.entries(assetClassGroups).forEach(([ac, acFunds]) => {
      totals[ac] = acFunds.reduce((sum, f) => sum + (f.weight || 0), 0);
    });
    return totals;
  }, [assetClassGroups]);

  const handleEqualWeight = () => {
    if (funds.length === 0) return;
    const weight = Math.round((100 / funds.length) * 100) / 100;
    funds.forEach((f, i) => {
      // Give the last fund the remainder to ensure exact 100%
      const w = i === funds.length - 1
        ? Math.round((100 - weight * (funds.length - 1)) * 100) / 100
        : weight;
      onUpdateWeight(f.fund_code, w);
    });
  };

  const handleNormalise = () => {
    // Scaling by 100/net flips every sign when the book nets negative —
    // refuse rather than silently invert a geared portfolio.
    if (totalWeight <= 0 || funds.length === 0) return;
    const scale = 100 / totalWeight;
    funds.forEach((f, i) => {
      if (i === funds.length - 1) {
        const otherWeights = funds.slice(0, -1).reduce(
          (sum, ff) => sum + Math.round(ff.weight * scale * 100) / 100,
          0
        );
        onUpdateWeight(f.fund_code, Math.round((100 - otherWeights) * 100) / 100);
      } else {
        onUpdateWeight(f.fund_code, Math.round(f.weight * scale * 100) / 100);
      }
    });
  };

  // Stored return_objective is a decimal; show it as a percent in the form.
  const objPercent = currentReturnObjective != null ? String(+(currentReturnObjective * 100).toFixed(4)) : '';

  const handleSaveClick = () => {
    setSaveName(currentPortfolioName || '');
    setSaveClient(currentClient || '');
    setSavePortfolioCode(currentPortfolioCode || '');
    setSaveDescription(currentDescription || '');
    setSaveObjectiveType(currentObjectiveType || 'cpi');
    setSaveReturnObjective(objPercent);
    setSaveTimeHorizon(currentTimeHorizon != null ? String(currentTimeHorizon) : '');
    setSaveError('');
    setIsSaveAs(false);
    setShowSaveModal(true);
  };

  const handleSaveAsClick = () => {
    setSaveName((currentPortfolioName || '') + ' (copy)');
    setSaveClient(currentClient || '');
    setSavePortfolioCode('');
    setSaveDescription(currentDescription || '');
    setSaveObjectiveType(currentObjectiveType || 'cpi');
    setSaveReturnObjective(objPercent);
    setSaveTimeHorizon(currentTimeHorizon != null ? String(currentTimeHorizon) : '');
    setSaveError('');
    setIsSaveAs(true);
    setShowSaveModal(true);
  };

  const handleSaveSubmit = async () => {
    if (!saveName.trim()) {
      setSaveError('Please enter a portfolio name');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        name: saveName.trim(),
        client: saveClient.trim() || null,
        portfolioCode: savePortfolioCode.trim() || null,
        description: saveDescription.trim() || null,
        objectiveType: saveObjectiveType || 'cpi',
        returnObjective: saveReturnObjective !== '' ? parseFloat(saveReturnObjective) / 100 : null,
        timeHorizon: saveTimeHorizon !== '' ? parseInt(saveTimeHorizon, 10) : null,
      };
      if (isSaveAs) {
        await onSaveAs(payload);
      } else {
        await onSave(payload);
      }
      setShowSaveModal(false);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (funds.length === 0) {
    return (
      <div className="empty-state">
        <p className="mb-1" style={{ fontSize: '1.1rem' }}>No funds selected</p>
        <p className="mb-0" style={{ fontSize: '0.85rem' }}>
          Search for funds on the left and click + to add them to your portfolio.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Gearing toggle — negative weights are only accepted when this is on */}
      <div className={`gearing-toggle${gearingEnabled ? ' on' : ''}`}>
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id="gearingSwitch"
            checked={gearingEnabled}
            onChange={(e) => onToggleGearing(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="gearingSwitch">
            <strong>Gearing</strong> — allow negative weights (borrowed / short positions)
          </label>
        </div>
        {gearingEnabled && (
          <span className="gearing-toggle-note">
            Long legs may exceed 100%; negative legs offset them. The portfolio must still <strong>net to 100%</strong>.
          </span>
        )}
      </div>

      <div className="builder-holdings-table" style={{ overflowX: 'auto' }}>
        <table className="holdings-table">
          <thead>
            <tr>
              <th className="name-col">Fund Name</th>
              <th className="code-col">Code</th>
              <th className="weight-col">Weight (%)</th>
              <th style={{ width: '36px' }}></th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(assetClassGroups).map(([assetClass, acFunds], groupIndex) => (
              <React.Fragment key={assetClass}>
                {/* Asset Class Header Row */}
                <tr className="asset-class-header">
                  <td colSpan={2}>
                    <span
                      className="asset-class-indicator"
                      style={{ backgroundColor: getAssetClassColor(assetClass, groupIndex) }}
                    />
                    {assetClass}
                  </td>
                  <td className="weight-col asset-class-total">
                    {assetClassTotals[assetClass]?.toFixed(1) || '0.0'}%
                  </td>
                  <td></td>
                </tr>
                {/* Fund rows */}
                {acFunds.map(fund => (
                  <tr
                    key={fund.fund_code}
                    className={`holding-row${!fund.weight ? ' zero-weight' : ''}${(fund.weight || 0) < 0 ? ' negative-weight' : ''}`}
                  >
                    <td className="name-col">
                      {fund.fund_name || fund.fund_code}
                      {(fund.weight || 0) < 0 && (
                        <span className="negative-chip" title="Negative weight — borrowed / short position (gearing)">
                          GEARED −
                        </span>
                      )}
                    </td>
                    <td className="code-col">{fund.fund_code}</td>
                    <td className="weight-col">
                      <input
                        type="number"
                        className="weight-input"
                        value={fund.weight || ''}
                        onChange={(e) => onUpdateWeight(fund.fund_code, parseFloat(e.target.value) || 0)}
                        min={gearingEnabled ? -100 : 0}
                        max={gearingEnabled ? 200 : 100}
                        step="0.5"
                      />
                    </td>
                    <td>
                      <button
                        className="btn-remove"
                        onClick={() => onRemoveFund(fund.fund_code)}
                        title="Remove fund"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}

            {/* Total Row */}
            <tr className="total-row">
              <td colSpan={2}>Total</td>
              <td className="weight-col">{totalWeight.toFixed(1)}%</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Weight bar — geared books show gross exposure: long segments first,
          negative legs as red striped segments. Widths are scaled by gross so
          the whole book is visible rather than negatives vanishing. */}
      <div className="weight-bar">
        {(() => {
          const scale = hasNegativeWeights && grossWeight > 0 ? 100 / grossWeight : 1;
          const longs = [];
          const shorts = [];
          Object.entries(assetClassGroups).forEach(([assetClass, acFunds]) =>
            acFunds.forEach(fund => {
              const w = fund.weight || 0;
              const seg = (
                <div
                  key={fund.fund_code}
                  className={`segment${w < 0 ? ' negative' : ''}`}
                  style={{
                    width: `${Math.min(Math.abs(w) * scale, 100)}%`,
                    background: w < 0 ? undefined : getAssetClassColor(assetClass, 0),
                  }}
                  title={`${fund.fund_name || fund.fund_code}: ${w}%${w < 0 ? ' (geared)' : ''}`}
                />
              );
              (w < 0 ? shorts : longs).push(seg);
            })
          );
          return [...longs, ...shorts];
        })()}
      </div>

      {/* Weight total — geared books get a loud amber state with net/gross */}
      <div className={`weight-total ${isValid ? (hasNegativeWeights ? 'geared' : 'valid') : 'invalid'}`}>
        {hasNegativeWeights ? (
          <span>
            <strong>⚠ GEARED</strong> · Net: {totalWeight.toFixed(2)}% ·
            Gross: {grossWeight.toFixed(1)}% · Gearing: {gearingLevel.toFixed(1)}%
          </span>
        ) : (
          <span>Total: {totalWeight.toFixed(2)}%</span>
        )}
        {isValid ? (
          <span style={{ fontSize: '0.8rem' }}>
            {hasNegativeWeights
              ? 'Nets to 100% with negative (borrowed/short) positions'
              : 'Ready to save'}
          </span>
        ) : negativesBlocked ? (
          <span style={{ fontSize: '0.8rem' }}>Negative weights need the Gearing toggle enabled</span>
        ) : grossTooHigh ? (
          <span style={{ fontSize: '0.8rem' }}>Gross exposure over the 300% gearing cap</span>
        ) : hasZeroWeightFunds ? (
          <span style={{ fontSize: '0.8rem' }}>All funds must have a non-zero weight</span>
        ) : (
          <span style={{ fontSize: '0.8rem' }}>Must {hasNegativeWeights ? 'net' : 'equal'} 100%</span>
        )}
      </div>

      {/* Actions */}
      <div className="builder-actions">
        <button className="btn btn-outline-secondary" onClick={handleEqualWeight}>
          Equal Weight
        </button>
        <button
          className="btn btn-outline-secondary"
          onClick={handleNormalise}
          disabled={totalWeight <= 0}
          title={totalWeight <= 0 ? 'Cannot normalise a book that nets to zero or below' : undefined}
        >
          Normalise
        </button>
        <button
          className="btn"
          style={{ background: 'var(--primary-color)', color: 'var(--white-color)' }}
          onClick={handleSaveClick}
          disabled={!isValid}
        >
          {currentPortfolioId ? 'Save Changes' : 'Save Portfolio'}
        </button>
        {currentPortfolioId && (
          <button
            className="btn btn-outline-secondary"
            onClick={handleSaveAsClick}
            disabled={!isValid}
          >
            Save As
          </button>
        )}
      </div>

      {/* Save modal */}
      {showSaveModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setShowSaveModal(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content save-modal">
              <div className="modal-header" style={{ background: 'var(--primary-color)', color: 'var(--white-color)' }}>
                <h5 className="modal-title" style={{ fontSize: '1rem' }}>
                  {isSaveAs ? 'Save As New Portfolio' : currentPortfolioId ? 'Update Portfolio' : 'Save Portfolio'}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowSaveModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Portfolio Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder="e.g. Growth Portfolio v2"
                    maxLength={200}
                    autoFocus
                  />
                </div>
                <div className="row mb-3">
                  <div className="col">
                    <label className="form-label">Client (optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={saveClient}
                      onChange={(e) => setSaveClient(e.target.value)}
                      placeholder="e.g. Smith Family"
                      maxLength={200}
                    />
                  </div>
                  <div className="col">
                    <label className="form-label">Portfolio Code (optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={savePortfolioCode}
                      onChange={(e) => setSavePortfolioCode(e.target.value)}
                      placeholder="e.g. SF-GROWTH-01"
                      maxLength={50}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Description (optional)</label>
                  <textarea
                    className="form-control"
                    value={saveDescription}
                    onChange={(e) => setSaveDescription(e.target.value)}
                    rows={2}
                    placeholder="Notes about this portfolio..."
                  />
                </div>
                <div className="mb-1">
                  <label className="form-label">Return Objective (optional)</label>
                  <small className="text-muted d-block mb-1" style={{ fontSize: '0.72rem' }}>
                    Compared against the CMA expected return in the Compare tab.
                  </small>
                </div>
                <div className="row mb-3">
                  <div className="col-4">
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Type</label>
                    <select
                      className="form-select"
                      value={saveObjectiveType}
                      onChange={(e) => setSaveObjectiveType(e.target.value)}
                    >
                      <option value="cpi">CPI+</option>
                      <option value="cash">Cash+</option>
                      <option value="absolute">Absolute</option>
                    </select>
                  </div>
                  <div className="col-4">
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>
                      {saveObjectiveType === 'absolute' ? 'Return (%)' : saveObjectiveType === 'cash' ? 'Cash+ (%)' : 'CPI+ (%)'}
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={saveReturnObjective}
                      onChange={(e) => setSaveReturnObjective(e.target.value)}
                      min="0"
                      max="20"
                      step="0.25"
                      placeholder="e.g. 3.5"
                    />
                  </div>
                  <div className="col-4">
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Horizon (yrs)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={saveTimeHorizon}
                      onChange={(e) => setSaveTimeHorizon(e.target.value)}
                      min="1"
                      max="30"
                      step="1"
                      placeholder="e.g. 7"
                    />
                  </div>
                </div>
                {saveError && (
                  <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>
                    {saveError}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowSaveModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-sm"
                  style={{ background: 'var(--primary-color)', color: 'var(--white-color)' }}
                  onClick={handleSaveSubmit}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PortfolioBuilder;
